/* /preview/avatars — avatar selection gallery
   ────────────────────────────────────────────────
   Side-by-side comparison of the 4 gesture-friendly presenter avatars
   under consideration for the Verqo landing hero. Each card shows the
   live HeyGen preview loop so you can see hand movement + facial
   expression before we spend credits on a full 3-minute render.
*/
import { useRef, useState } from "react";
import { CheckCircle2, Copy, Volume2, VolumeX } from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";

const AVATARS = [
  {
    id:   "Brandon_Business_Standing_Front_public",
    name: "Brandon · Currently Rendered",
    vibe: "The 3-min Verqo pitch you already watched · hands stay down · calm energy",
    tag:  "Current",
    tagColor: "current",
    preview: "/media/verqo-hero.mp4",
    poster:  "/media/verqo-hero-poster.jpg",
    isCurrent: true,
  },
  {
    id:   "Brandon_expressive2_public",
    name: "Brandon · Grey Suit · Expressive",
    vibe: "Same face + suit · v3 expressive tier · hands actively gesture up + around · matches script emotion",
    tag:  "Recommended · matches your look",
    tagColor: "lime",
    preview: "https://files2.heygen.ai/avatar/v3/5f57202f32dc4604845dc3da6372e5e4_53100/preview_video_target.mp4",
    poster:  "https://files2.heygen.ai/avatar/v3/5f57202f32dc4604845dc3da6372e5e4_53100/preview_target.webp",
  },
  {
    id:   "Brandon_expressive_public",
    name: "Brandon · White Shirt · Expressive",
    vibe: "Same face, dressed-down · lighter feel · same expressive hand gesture range",
    tag:  "Alt outfit",
    tagColor: "sky",
    preview: "https://files2.heygen.ai/avatar/v3/0978953f30744f8dbf7100b60d2a09b9_53210/preview_video_target.mp4",
    poster:  "https://files2.heygen.ai/avatar/v3/0978953f30744f8dbf7100b60d2a09b9_53210/preview_target.webp",
  },
  {
    id:   "Brandon_expressive3_public",
    name: "Brandon · Blue Sweater · Expressive",
    vibe: "Same face, most casual outfit · warm energy · expressive gestures",
    tag:  "Alt outfit",
    tagColor: "amber",
    preview: "https://files2.heygen.ai/avatar/v3/60eee91ed9164189bca7b2cfe7d9f25f_53110/preview_video_target.mp4",
    poster:  "https://files2.heygen.ai/avatar/v3/60eee91ed9164189bca7b2cfe7d9f25f_53110/preview_target.webp",
  },
];

const TAG_STYLES = {
  current: "bg-slate-900 text-lime-300 border-slate-800",
  lime:    "bg-lime-50 text-lime-700 border-lime-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  violet:  "bg-violet-50 text-violet-700 border-violet-200",
};

export default function AvatarPreview() {
  const [picked, setPicked] = useState(null);
  const [copied, setCopied] = useState(false);
  // Only ONE preview plays with sound at a time — clicking a second card
  // mutes the first so we don't overlap voices.
  const [unmutedId, setUnmutedId] = useState(null);
  const videoRefs = useRef({});

  const copyId = (id) => {
    navigator.clipboard.writeText(id);
    setCopied(id);
    setTimeout(() => setCopied(false), 1500);
  };

  const toggleAudio = (id) => {
    // Mute all other videos + toggle this one.
    Object.entries(videoRefs.current).forEach(([k, v]) => {
      if (!v) return;
      if (k === id) {
        v.muted = !v.muted;
        if (!v.muted) v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        v.muted = true;
      }
    });
    const v = videoRefs.current[id];
    setUnmutedId(v && !v.muted ? id : null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <VerqoLockup size="sm" />
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-slate-500 mono">
            Avatar picker · pre-render preview
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        <div className="text-[10.5px] uppercase tracking-[0.24em] text-lime-600 mono">
          Choose your presenter
        </div>
        <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
          Pick the avatar that best fits Zev's founder pitch.
        </h1>
        <p className="mt-3 text-[15px] text-slate-600 max-w-2xl">
          The first card is the <strong>Brandon avatar you just rendered</strong> reading the actual Verqo script — click to unmute and compare his voice + energy against the 4 gesture-friendly Javi variants. Only one plays at a time.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="avatar-grid">
          {AVATARS.map((a, i) => (
            <div
              key={a.id}
              className={`group rounded-2xl overflow-hidden border transition-all bg-white
                          ${picked === a.id ? "border-lime-500 ring-2 ring-lime-500/40 shadow-lg" : "border-slate-200 hover:border-slate-300 hover:shadow-md"}`}
              data-testid={`avatar-card-${i}`}
            >
              <div className="relative aspect-video bg-black">
                <video
                  ref={el => { videoRefs.current[a.id] = el; }}
                  src={a.preview}
                  poster={a.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid={`avatar-video-${i}`}
                />
                <div className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] mono uppercase tracking-[0.16em] border ${TAG_STYLES[a.tagColor]}`}>
                  {a.tag}
                </div>
                {/* Unmute pill — one video at a time. */}
                <button
                  onClick={() => toggleAudio(a.id)}
                  className={`absolute bottom-3 right-3 rounded-full backdrop-blur px-3 py-1.5 text-[11px] mono uppercase tracking-[0.14em] flex items-center gap-1.5 transition-colors
                              ${unmutedId === a.id
                                ? "bg-lime-500 text-black hover:bg-lime-400"
                                : "bg-black/60 text-white hover:bg-black/80"}`}
                  data-testid={`unmute-${i}`}
                  aria-label={unmutedId === a.id ? "Mute preview" : "Unmute preview"}
                >
                  {unmutedId === a.id
                    ? <><Volume2 className="w-3 h-3"/> Playing</>
                    : <><VolumeX className="w-3 h-3"/> Click to unmute</>}
                </button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <div className="text-base font-semibold text-slate-900 tracking-tight">{a.name}</div>
                  <div className="mt-1 text-[13px] text-slate-600">{a.vibe}</div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => copyId(a.id)}
                    className="text-[11px] mono text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
                    data-testid={`copy-id-${i}`}
                  >
                    {copied === a.id ? <><CheckCircle2 className="w-3 h-3 text-lime-500"/> Copied!</> : <><Copy className="w-3 h-3"/> {a.id}</>}
                  </button>
                  <button
                    onClick={() => setPicked(a.id)}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium tracking-tight border transition-colors
                                 ${picked === a.id
                                    ? "bg-lime-500 text-white border-lime-500"
                                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-400 hover:bg-slate-50"}`}
                    data-testid={`pick-${i}`}
                  >
                    {picked === a.id ? "✓ Picked" : "Pick this one"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {picked && (
          <div className="mt-10 rounded-xl border border-lime-500/40 bg-lime-50 p-5 flex items-center justify-between" data-testid="pick-summary">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 mono">You picked</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {AVATARS.find(a => a.id === picked)?.name}
              </div>
              <div className="mt-1 text-[12.5px] mono text-slate-600">{picked}</div>
            </div>
            <div className="text-[13px] text-slate-700 max-w-md">
              Reply in chat with your updated script and:<br/>
              <strong className="mono text-lime-800">"Re-render with {picked}"</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
